<script setup>
import { computed, onMounted, onUnmounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { getComponentByType } from "@/features/filters";
import { useFiltersStore } from "@/store";
import { McLabel, McIcon } from "@maersk-global/mds-components-core";

const props = defineProps({
  recipe: {
    type: Array,
    required: true
  },
  filterType: {
    type: String,
    required: true
  },
  stack: {
    type: String,
    default: "vertical"
  },
  zonePrefix: {
    type: String,
    default: ""
  },
  forceAppliedDefault: {
    type: Boolean,
    default: true,
    required: false
  },
  disabled: {
    type: Boolean,
    default: false,
    required: false
  }
});

const { t } = useI18n();
const filterStore = useFiltersStore();
const { applyFilters, filtersApplied, setFilterSearchTerm } = filterStore || {};

const structuredFilters = computed(() => {
  const sections = {};

  for (const filter of props.recipe) {
    if (filter.hide) continue;

    const section = filter.sectionLabel || "_no_section";
    const group = filter.groupId || "_no_group";

    if (!sections[section]) {
      sections[section] = {
        icon: filter.sectionIcon || "",
        groups: {}
      };
    }

    if (!sections[section].groups[group]) {
      sections[section].groups[group] = {
        label: filter.groupLabel || "",
        filters: []
      };
    }

    sections[section].groups[group].filters.push(filter);
  }

  return sections;
});

const resolveDependencies = (filter) => {
  const result = {};
  if (!filter.dependencies) return result;

  for (const [param, key] of Object.entries(filter.dependencies)) {
    const fullKey = props.zonePrefix ? `${props.zonePrefix}.${key}` : key;
    const [zone, filterKey] = fullKey.includes(".") ? fullKey.split(".") : ["default", fullKey];
    result[param] = filtersApplied[zone]?.[filterKey];
  }
  return result;
};

const applyDefaults = () => {
  if (!props.forceAppliedDefault) {
    return;
  }
  props.recipe.forEach((filter) => {
    if (filter.value === undefined) return;

    const fullKey = props.zonePrefix ? `${props.zonePrefix}.${filter.key}` : filter.key;
    const [zone, filterKey] = fullKey.includes(".") ? fullKey.split(".") : ["default", fullKey];

    if (filtersApplied[zone]?.[filterKey]) return;

    const filterPayload = {
      ...filter.value,
      filterKey: filter.key,
      filterType: props.filterType
    };

    ["groupId", "groupLabel", "sectionLabel", "label"].forEach((prop) => {
      if (filter[prop]) {
        filterPayload[prop === "label" ? "filterLabel" : prop] = filter[prop];
      }
    });

    applyFilters(zone, filterKey, filterPayload);
  });
};

onUnmounted(() => {
  setFilterSearchTerm(""); // Reset the search input when panel closes
});

onMounted(() => {
  applyDefaults();
});

watch(
  () => props.zonePrefix,
  () => {
    applyDefaults();
  }
);
</script>

<template>
  <div class="filter-container" :class="`stack-${props.stack}`" data-test="filter-renderer">
    <!-- Section loop -->
    <template v-for="(section, sectionLabel) in structuredFilters" :key="sectionLabel">
      <div
        v-if="sectionLabel !== '_no_section'"
        class="filter-section"
        :data-test="`filter-section-${sectionLabel.toLowerCase().replace(/\s+/g, '-')}`"
      >
        <mc-label
          class="filter-section-title"
          :data-test="`filter-section-title-${sectionLabel.toLowerCase().replace(/\s+/g, '-')}`"
        >
          <span class="section-icon-label">
            <mc-icon
              v-if="section.icon"
              :icon="section.icon"
              class="section-icon"
              :data-test="`filter-section-icon-${sectionLabel.toLowerCase().replace(/\s+/g, '-')}`"
            />
            {{ t(sectionLabel || "") }}
          </span>
        </mc-label>

        <!-- Group loop -->
        <template v-for="(group, groupId) in section.groups" :key="groupId">
          <div v-if="groupId !== '_no_group'" class="filter-group" :data-test="`filter-group-${groupId}`">
            <mc-label
              v-if="group.label"
              class="filter-group-title mds-font--small"
              :data-test="`filter-group-title-${groupId}`"
            >
              {{ t(group.label || "") }}
            </mc-label>

            <div class="filter-items">
              <div
                v-for="filter in group.filters"
                :key="filter.key"
                class="filter-item"
                :data-test="`filter-item-${filter.key}`"
              >
                <component
                  :is="getComponentByType(filter.type)"
                  v-show="!filter.hide"
                  :key="`${zonePrefix}-${filter.key}`"
                  :data-test="`filter-${filter.type}-${filter.key}`"
                  :filter="filter"
                  :filter-type="filterType"
                  :zone-prefix="zonePrefix"
                  v-bind="{
                    ...(disabled ? { disabled: true } : {}),
                    ...(filter.dependencies ? { dependencies: resolveDependencies(filter) } : {})
                  }"
                />
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Flat fallback -->
      <template v-else>
        <template v-for="(group, groupId) in section.groups" :key="groupId">
          <div class="filter-items">
            <div
              v-for="filter in group.filters"
              :key="filter.key"
              class="filter-item"
              :data-test="`filter-item-${filter.key}`"
            >
              <component
                :is="getComponentByType(filter.type)"
                v-show="!filter.hide"
                :key="`${zonePrefix}-${filter.key}`"
                :data-test="`filter-${filter.type}-${filter.key}`"
                :filter="filter"
                :filter-type="filterType"
                :zone-prefix="zonePrefix"
                v-bind="{
                  ...(disabled ? { disabled: true } : {}),
                  ...(filter.dependencies ? { dependencies: resolveDependencies(filter) } : {})
                }"
              />
            </div>
          </div>
        </template>
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
.filter-container.stack-vertical .filter-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filter-container.stack-horizontal .filter-items {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 12px;
  height: 100%;
}

.section-icon-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: var(--mds_brand_typography_text_medium_bold_font-weight);
}

.section-icon {
  font-size: 18px;
}

.filter-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  border-bottom: 1px solid var(--mds_brand_appearance_neutral_weak_background-color);
  padding: 20px;

  &:last-child {
    border-bottom: none;
  }

  &:not(:has(.filter-section-title)) {
    gap: 0;
  }
}

.filter-group-title {
  margin-top: 12px;
  margin-bottom: 4px;
  font-size: var(--mds_brand_typography_text_x-small_desktop_font-size);
}
</style>
